import { useState, type FormEvent } from "react";
import Field from "@/components/base/Field";
import Honeypot from "@/components/base/Honeypot";
import FormStatus from "@/components/base/FormStatus";
import ActionButton from "@/components/base/ActionButton";
import { useEnquirySubmit } from "@/hooks/useEnquirySubmit";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahppnrp14a1h3mr6q80";
const HONEYPOT = "company_alt";

export default function FleetEnquiryForm() {
  const { status, message, submit, reset } = useEnquirySubmit();
  const [referenceId, setReferenceId] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const reference = `KLZ-FL-${Date.now().toString(36).toUpperCase().slice(-5)}`;
    setReferenceId(reference);
    submit(event.currentTarget, SUBMIT_ADDR, HONEYPOT);
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <FormStatus status={status} message={message} referenceId={referenceId} />
        <button
          type="button"
          onClick={() => {
            reset();
            setReferenceId("");
          }}
          className="mt-6 inline-flex cursor-pointer items-center gap-2 font-label text-sm font-semibold text-primary-700 hover:text-primary-800"
        >
          <i className="ri-refresh-line" /> Submit another fleet enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      id="fleet-enquiry-form"
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <Honeypot name={HONEYPOT} />
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label="Full Name" name="name" required placeholder="Your full name" autoComplete="name" />
        <Field label="Company / Organisation" name="company" required placeholder="Company name" autoComplete="organization" />
        <Field label="Mobile Number" name="mobile" variant="tel" required placeholder="10-digit mobile number" autoComplete="tel" />
        <Field label="Email" name="email" variant="email" required placeholder="you@company.com" autoComplete="email" />
        <Field label="City" name="city" required placeholder="Operating city" autoComplete="address-level2" />
        <Field
          label="Fleet Size"
          name="fleet_size"
          variant="select"
          required
          options={[
            { value: "1-5", label: "1 – 5 vehicles" },
            { value: "6-20", label: "6 – 20 vehicles" },
            { value: "21-50", label: "21 – 50 vehicles" },
            { value: "51-200", label: "51 – 200 vehicles" },
            { value: "200-plus", label: "200+ vehicles" },
          ]}
        />
        <Field
          label="Primary Vehicle Type"
          name="vehicle_type"
          variant="select"
          required
          options={[
            { value: "2-wheeler", label: "2-Wheeler" },
            { value: "3-wheeler", label: "3-Wheeler" },
            { value: "4-wheeler", label: "4-Wheeler" },
            { value: "lcv", label: "Light Commercial Vehicle" },
            { value: "mixed", label: "Mixed Fleet" },
          ]}
        />
        <Field label="Operating Region" name="operating_region" placeholder="e.g. Pune & Pimpri-Chinchwad" />
        <Field
          label="Tell us about your fleet requirement"
          name="message"
          variant="textarea"
          rows={4}
          maxLength={500}
          placeholder="Routes, duty cycles, average daily distance, electrification goals (max 500 characters)"
          className="md:col-span-2"
        />
      </div>

      <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <ActionButton type="submit" variant="primary" size="lg" icon="ri-roadster-line" disabled={status === "submitting"}>
          {status === "submitting" ? "Submitting…" : "Request Fleet Assessment"}
        </ActionButton>
        <p className="text-[11px] leading-relaxed text-foreground-500">
          Fleet plans are tailored after assessment of vehicles, routes and charging needs.
        </p>
      </div>

      <FormStatus status={status} message={message} />
    </form>
  );
}